import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Trophy, Medal, Crown, Award, ShieldCheck, Star, Loader2, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_STYLES = {
  1: { icon: Crown, bg: 'bg-gradient-to-r from-amber-400 to-yellow-300', text: 'text-amber-900', ring: 'ring-2 ring-amber-400/50' },
  2: { icon: Medal, bg: 'bg-gradient-to-r from-gray-300 to-slate-200', text: 'text-gray-700', ring: 'ring-2 ring-gray-300/50' },
  3: { icon: Medal, bg: 'bg-gradient-to-r from-amber-600 to-orange-400', text: 'text-amber-900', ring: 'ring-2 ring-amber-500/50' },
};

export default function CertificateLeaderboardPage() {
  const [data, setData] = useState({ leaderboard: [], total_certs: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/platform/certificates/leaderboard/`)
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="leaderboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div data-testid="certificate-leaderboard-page">
      {/* Hero */}
      <section className="relative py-16 bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847] overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="container-app relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-sm font-medium mb-4">
            <Trophy className="w-4 h-4" /> STEM Achievement Rankings
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-3">
            Certificate <span className="text-rsc-yellow">Leaderboard</span>
          </h1>
          <p className="text-white/60 max-w-lg mx-auto text-base">
            Celebrating our top STEM achievers. Every certificate represents verified skills and dedication.
          </p>
          <div className="flex justify-center gap-6 mt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-rsc-yellow">{data.total_certs}</p>
              <p className="text-xs text-white/40">Certificates Issued</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{data.leaderboard.length}</p>
              <p className="text-xs text-white/40">Certified Learners</p>
            </div>
          </div>
        </div>
      </section>

      {/* Leaderboard */}
      <section className="py-10 bg-gray-50 min-h-[40vh]">
        <div className="container-app max-w-3xl">
          {data.leaderboard.length === 0 ? (
            <Card className="border-dashed border-2">
              <CardContent className="p-12 text-center">
                <ShieldCheck className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-semibold text-gray-700 mb-2">No Certificates Yet</h3>
                <p className="text-sm text-gray-500">Complete STEM milestones to earn verified certificates and appear on the leaderboard!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="leaderboard-list">
              {/* Top 3 Podium */}
              {data.leaderboard.length >= 3 && (
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[1, 0, 2].map(idx => {
                    const entry = data.leaderboard[idx];
                    if (!entry) return null;
                    const style = RANK_STYLES[entry.rank];
                    const Icon = style?.icon || Award;
                    return (
                      <div key={entry.rank} className={`text-center ${idx === 1 ? '-mt-4' : 'mt-4'}`}>
                        <div className={`w-14 h-14 rounded-full ${style?.bg || 'bg-gray-100'} flex items-center justify-center mx-auto mb-2 shadow-lg ${style?.ring || ''}`}>
                          <Icon className={`w-7 h-7 ${style?.text || 'text-gray-600'}`} />
                        </div>
                        <p className="font-bold text-sm text-gray-900">{entry.name}</p>
                        <p className="text-xs text-gray-500">{entry.cert_count} cert{entry.cert_count !== 1 ? 's' : ''}</p>
                        <p className="text-xs text-indigo-500 font-medium">{entry.avg_score}% avg</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Full List */}
              {data.leaderboard.map((entry) => {
                const style = RANK_STYLES[entry.rank];
                const Icon = style?.icon || Award;
                return (
                  <div
                    key={entry.rank}
                    className={`flex items-center gap-4 p-4 bg-white rounded-xl border hover:shadow-md transition-shadow ${style?.ring || ''}`}
                    data-testid={`leaderboard-rank-${entry.rank}`}
                  >
                    {/* Rank */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      style ? `${style.bg} shadow-md` : 'bg-gray-100'
                    }`}>
                      {entry.rank <= 3 ? (
                        <Icon className={`w-5 h-5 ${style?.text || ''}`} />
                      ) : (
                        <span className="text-sm font-bold text-gray-500">#{entry.rank}</span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900">{entry.name}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {entry.tracks.map(t => (
                          <Badge key={t} variant="outline" className="text-[10px] px-1.5 py-0 border-indigo-200 text-indigo-600">{t}</Badge>
                        ))}
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-4 flex-shrink-0 text-right">
                      <div>
                        <p className="text-lg font-bold text-gray-900">{entry.cert_count}</p>
                        <p className="text-[10px] text-gray-400 uppercase">Certs</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-indigo-600">{entry.avg_score}%</p>
                        <p className="text-[10px] text-gray-400 uppercase">Avg Score</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
